<template>
  <UserDataProvider
    @error="onError"
    :manage-lifecycle="true"
    v-slot="{loaded, profile, accountStatus, isAuthenticated, user, scopes}"
  >
    <v-app id="dashboard" light>
      <template v-if="!error">
        <AccountStatus
          v-if="!loaded && accountStatus !== 'OK'"
          :name="profile?.name ?? ''"
          :status="accountStatus"
        />
        <AccountStatus
          v-else-if="loaded && accountStatus !== 'OK'"
          :name="profile?.name ?? ''"
          :status="accountStatus"
        />
        <template v-else-if="isAuthenticated && user && loaded && accountStatus === 'OK'">
          <AppBar
            clipped-right
          />
          <NavigationDrawer
            left
            toggleId="main-navigation"
            :width="leftDrawerWidth"
            mobile-breakpoint="0"
            temporary
          >
            <NavMenu :items="menuItems" />
            <template v-slot:header>
              <NavHeader />
            </template>
            <template v-slot:footer>
              <NavFooter :externals="externals"/>
            </template>
          </NavigationDrawer>
          <NavigationDrawer
            right
            toggleId="right-drawer"
            temporary
            clipped
            mobile-breakpoint="0"
          >
            <portal-target name="right-drawer" multiple>
              <div>Tirroir droit</div>
            </portal-target>
          </NavigationDrawer>
          <ErrorView v-if="error" :error="error" />
          <template v-else>
            <RealtimeProvider
              :user="user"
              :manage-lifecycle="true"
              v-slot="{ state: hubState }"
            >
              <TicketsStoreProvider
                :hubStarted="hubState.started"
                :manage-lifecycle="true"
                :scopes="scopes"
                v-slot="{ methods: { loadAdditional}}"
              >
                <ViewAsProvider
                  :user="user"
                  :manage-lifecycle="true"
                  @change="(v) => loadAdditional(v)"
                >
                  <router-view/>
                </ViewAsProvider>
              </TicketsStoreProvider>
            </RealtimeProvider>
            <snack-messages bottom left :timeout="-1"/>
          </template>
        </template>
        <template v-else-if="!isAuthenticated && $route.path == '/logout'">
          <router-view/>
        </template>
      </template>
      <template v-else>
        <ErrorView :error="error" />
      </template>
    </v-app>
  </UserDataProvider>
</template>

<script>
import UserDataProvider, { UsersRoles } from '@/components/frames/UserDataProvider'
import RealtimeProvider from '@/components/frames/RealtimeProvider'
import TicketsStoreProvider from '@/components/frames/TicketsStoreProvider'
import ViewAsProvider from '@/components/frames/ViewAsProvider'
import AppBar from '@/components/common/AppBar.vue'
import NavigationDrawer from '@/components/common/NavigationDrawer.vue'
import NavMenu from '@/components/common/NavMenu.vue'
import NavFooter from '@/components/common/NavFooter.vue'
import NavHeader from '@/components/common/NavHeader.vue'
import ErrorView from '@/components/common/ErrorView.vue'
import AccountStatus from '@/views/AccountStatus.vue'

import {
  mdiTextBoxOutline,
  mdiTableCheck,
  // mdiCalendarSearch,
  mdiMicrosoftSharepoint
} from '@mdi/js'

export default {
  name: 'App',
  components: {
    ErrorView,
    AppBar,
    NavigationDrawer,
    NavMenu,
    NavFooter,
    NavHeader,
    AccountStatus,
    UserDataProvider,
    RealtimeProvider,
    TicketsStoreProvider,
    ViewAsProvider
  },
  data: () => ({
    menuItems: [
      {
        icon: mdiTextBoxOutline,
        text: {
          fr: 'Billets',
          en: 'Tickets'
        },
        to: {
          name: 'tickets'
        }
      },
      // {
      //   icon: mdiCalendarSearch,
      //   text: {
      //     fr: 'Recherche avancée',
      //     en: 'Advanced search'
      //   },
      //   to: {
      //     name: 'advanced-search'
      //   },
      //   disabled: false
      // },
      {
        icon: mdiTableCheck,
        text: {
          fr: 'Surveillance',
          en: 'Monitoring'
        },
        to: {
          name: 'monitoring'
        },
        accessType: ['CPU_ACCESS'],
        roles: [UsersRoles.CPU_ADMIN, UsersRoles.CPU_REP_CA, UsersRoles.DEVOPS],
        disabled: false
      }
    ],
    externals: [
      {
        icon: mdiMicrosoftSharepoint,
        text: {
          fr: 'Procédure et solutions',
          en: 'Procedure and solutions'
        },
        href: 'https://compugensoftware.sharepoint.com/sites/ProceduresSolutions',
        target: '_blank',
        accessType: [], // [] = ALL ['PARTNER_ACCESS', 'CPU_ACCESS', 'CLIENT_ACCESS'],
        disabled: false
      },
      {
        icon: mdiMicrosoftSharepoint,
        text: {
          fr: 'Site des partenaires',
          en: 'Partners website'
        },
        href: 'https://cpunetwork.sharepoint.com/sites/Partners',
        target: '_blank',
        accessType: [], // [] = ALL ['PARTNER_ACCESS', 'CPU_ACCESS', 'CLIENT_ACCESS'],
        disabled: true
      }
    ],
    error: null
  }),
  computed: {
    leftDrawerWidth () {
      return this.$vuetify.breakpoint.smAndUp ? 300 : '85%'
    },
    authRequired () {
      return this.$route.meta.authRequired
    }
  },
  methods: {
    onError (value) {
      this.error = value
    }
  }
}
</script>

<style lang="scss">
html,
body,
#dashboard {
  width: 100%;
  height: 100%;
  overflow: hidden !important;
  overscroll-behavior-y: none;
}

#dashboard {
  background-color: transparent;
}

.v-application--wrap {
  min-height: 100% !important;
}

.theme--light.v-navigation-drawer:not(.v-navigation-drawer--floating) .v-navigation-drawer__border {
    background-color: rgba(0, 0, 0, 0.30) !important;
}

.custom-card {
    border: 1px solid rgba(0, 0, 0, 0.4) !important;
}

.custom-card.custom-card-error {
    border: 2px solid #B71C1C !important;
}

.custom-card-legend {
    position: absolute;
    top: 8px;
    left: 0px;
    background: white;
    font-weight: 400;
    padding: 0 0.3rem;
    line-height: 1;
    transform: translateY(-16px) scale(0.75);
    font-size: 16px;
}

.custom-card.custom-card-error .custom-card-legend {
    color: #B71C1C;
}

.custom-card.v-card--link:focus:before {
  opacity: 0;
}

.errorBody {
    white-space: pre-wrap;
    /* Since CSS 2.1 */
    white-space: -moz-pre-wrap;
    /* Mozilla, since 1999 */
    white-space: -pre-wrap;
    /* Opera 4-6 */
    white-space: -o-pre-wrap;
    /* Opera 7 */
    word-wrap: break-word;
    /* Internet Explorer 5.5+ */
    margin: 0;
    min-height: 355px;
    overflow-y: auto;
}

.view-body {
    width: 100%;
    height: 100%;
    overflow: hidden;
}

.v-application .overflow-hidden {
    overflow: hidden !important;
    max-width: 100% !important;
    max-height: 100% !important;
}
.v-application .overflow-y-hidden {
    overflow-y: hidden !important;
    max-height: 100% !important;
}
.v-application .overflow-x-hidden {
    overflow-x: hidden !important;
    max-width: 100% !important;
}
.v-application .flex-none {
    flex: none !important;
}

.with-background {
  background-image: linear-gradient(90deg, rgb(50 93 136 / 30%), rgb(255 255 255)), url(public/img/slide23.jpg);
  background-size: cover, cover;
  background-repeat: no-repeat, no-repeat;
  background-position: center, center, center, center;
}

.d-flex.gap-2 {
  gap: 8px;
}

</style>
